import { apiClient } from "../../shared/api/clients";
import { ApiResponse } from "../../shared/api/http";
import { resolveSelection } from "./dimensions";
import { createLimiter } from "./limiter";
import { createRequestCache, stableKey } from "./requestCache";
import {
  DimensionInfo,
  MdxQueryRequest,
  MeasureInfo,
  QueryResult,
} from "./types";

const cubePath = (cubeName: string) => `/cube/${encodeURIComponent(cubeName)}`;

export const getCubes = () => apiClient.get<string[]>("/cube");

// ── Caches (un appel identique n'est envoyé qu'une fois, voir requestCache.ts) ─
const MINUTES = 60_000;
const dimensionsCache = createRequestCache<ApiResponse<DimensionInfo[]>>({ ttlMs: Infinity });
const measuresCache = createRequestCache<ApiResponse<MeasureInfo[]>>({ ttlMs: Infinity });
const membersCache = createRequestCache<ApiResponse<string[]>>({ ttlMs: 10 * MINUTES, maxEntries: 200 });
const queryCache = createRequestCache<ApiResponse<QueryResult>>({ ttlMs: 5 * MINUTES, maxEntries: 50 });

/** Requêtes MDX simultanées max : au-delà, SSAS sature et les requêtes expirent. */
const MAX_CONCURRENT_QUERIES = 3;
const limitQuery = createLimiter(MAX_CONCURRENT_QUERIES);

/** Vide tous les caches du cube (ex. après retraitement du cube). */
export function clearCubeCache(): void {
  [dimensionsCache, measuresCache, membersCache, queryCache].forEach((c) => c.clear());
}

/** Copie propre à chaque appelant : un résultat partagé ne doit pas être muté (ni gelé par Redux). */
const cloneData = <T>(res: ApiResponse<T>): ApiResponse<T> => ({
  ...res,
  data: typeof structuredClone === "function" ? structuredClone(res.data) : JSON.parse(JSON.stringify(res.data ?? null)),
});

/** Dimensions du cube — mises en cache pour la session. */
export const getDimensions = (cubeName: string) =>
  dimensionsCache.get(cubeName, (signal) =>
    apiClient.get<DimensionInfo[]>(`${cubePath(cubeName)}/dimensions`, { signal }),
  );

/** Mesures du cube — mises en cache pour la session. */
export const getMeasures = (cubeName: string) =>
  measuresCache.get(cubeName, (signal) =>
    apiClient.get<MeasureInfo[]>(`${cubePath(cubeName)}/measures`, { signal }),
  );

/** Dimensions/hiérarchies réelles du cube ; un échec n'est pas mis en cache. */
export function getCubeMetadata(cubeName: string): Promise<DimensionInfo[]> {
  return getDimensions(cubeName).then(({ data }) => data ?? []);
}

/** Métadonnées si disponibles, sinon null (la requête part alors telle quelle). */
const metadataOrNull = (cubeName: string) => getCubeMetadata(cubeName).catch(() => null);

export const getMembers = async (
  cubeName: string,
  dimension: string,
  hierarchy: string,
  level: string,
) => {
  const dims = await metadataOrNull(cubeName);
  const sel = dims
    ? resolveSelection({ dimensionName: dimension, hierarchyName: hierarchy, levelName: level }, dims)
    : { dimensionName: dimension, hierarchyName: hierarchy, levelName: level };
  const params = { dimension: sel.dimensionName, hierarchy: sel.hierarchyName, level: sel.levelName };
  const res = await membersCache.get(stableKey([cubeName, params]), (signal) =>
    apiClient.get<string[]>(`${cubePath(cubeName)}/members`, { params, signal }),
  );
  return { ...res, data: [...(res.data ?? [])] };
};

type SelectionList = MdxQueryRequest["rows"];

/**
 * Exécute une requête MDX. Les noms de hiérarchies sont alignés sur ceux du
 * cube réel (anciens ou nouveaux) — rows, columns et filtres éventuels.
 * Une requête identique (en cours ou récente) n'est pas renvoyée au serveur.
 */
export const executeQuery = async (
  request: MdxQueryRequest,
  options: { signal?: AbortSignal } = {},
) => {
  const dims = await metadataOrNull(request.cubeName);
  let body: MdxQueryRequest = request;
  if (dims) {
    const fix = (list?: SelectionList) => list?.map((s) => resolveSelection(s, dims));
    const extra = request as MdxQueryRequest & { filters?: SelectionList };
    body = {
      ...request,
      rows: fix(request.rows) ?? [],
      columns: fix(request.columns) ?? [],
      ...(extra.filters ? { filters: fix(extra.filters) } : {}),
    };
  }
  const res = await queryCache.get(
    stableKey(body),
    (signal) => limitQuery(() => apiClient.post<QueryResult>("/cube/query", body, { signal }), signal),
    options.signal,
  );
  return cloneData(res);
};
